import { i18n } from '../../i18n/i18n.js'
import { Disclaimer } from './disclaimer.js'

export class AnalysisPanel {
  constructor(options = {}) {
    this.onBookConsultation = options.onBookConsultation || (() => {})
    this.element = null
    this.isOpen = false
    this.currentAnalysisData = null
    this.disclaimer = new Disclaimer()
  }

  render(container) {
    this.element = document.createElement('div')
    this.element.className = 'analysis-panel'
    this.element.setAttribute('role', 'complementary')
    this.element.setAttribute('aria-label', i18n.t('analysis.title'))

    this.element.innerHTML = `
      <div class="panel-handle" role="button" tabindex="0" aria-label="拖拽展开/收起">
        <div class="handle-bar"></div>
      </div>
      <div class="panel-content">
        <div class="panel-header">
          <h3 class="panel-title">${i18n.t('analysis.title')}</h3>
          <button class="close-btn" aria-label="${i18n.t('common.close')}">×</button>
        </div>
        <div class="panel-body">
          <div class="state empty-state" style="display: none;">
            <div class="empty-icon">🗺️</div>
            <p class="empty-text">${i18n.t('analysis.hint')}</p>
          </div>
          <div class="state loading-state" style="display: none;">
            <div class="spinner"></div>
            <p class="loading-text">${i18n.t('analysis.loading')}</p>
          </div>
          <div class="state result-state" style="display: none;">
            <div class="result-text"></div>
            <div class="disclaimer-container"></div>
            <button class="book-btn">
              ${i18n.t('consultation.bookButton')}
            </button>
          </div>
          <div class="state error-state" style="display: none;">
            <div class="error-icon">⚠️</div>
            <p class="error-text"></p>
          </div>
        </div>
      </div>
    `

    const disclaimerContainer = this.element.querySelector('.disclaimer-container')
    if (disclaimerContainer) {
      disclaimerContainer.appendChild(this.disclaimer.render())
    }

    this._bindEvents()
    container.appendChild(this.element)
  }

  _bindEvents() {
    const closeBtn = this.element.querySelector('.close-btn')
    closeBtn.addEventListener('click', () => this.close())

    const handle = this.element.querySelector('.panel-handle')
    this._setupDrag(handle)

    const bookBtn = this.element.querySelector('.book-btn')
    bookBtn.addEventListener('click', () => {
      if (this.currentAnalysisData) {
        this.onBookConsultation(this.currentAnalysisData)
      }
    })
  }

  _setupDrag(handle) {
    let startY = 0
    let startHeight = 0
    let isDragging = false

    const onTouchStart = (e) => {
      startY = e.touches[0].clientY
      startHeight = this.element.offsetHeight
      isDragging = true
      this.element.style.transition = 'none'
    }

    const onTouchMove = (e) => {
      if (!isDragging) return
      const deltaY = startY - e.touches[0].clientY
      const newHeight = Math.max(60, Math.min(startHeight + deltaY, window.innerHeight * 0.85))
      this.element.style.height = `${newHeight}px`
    }

    const onTouchEnd = () => {
      if (!isDragging) return
      isDragging = false
      this.element.style.transition = ''

      const currentHeight = this.element.offsetHeight
      const threshold = window.innerHeight * 0.25

      if (currentHeight < threshold) {
        this.close()
      } else {
        this.open()
      }
    }

    handle.addEventListener('touchstart', onTouchStart, { passive: true })
    handle.addEventListener('touchmove', onTouchMove, { passive: true })
    handle.addEventListener('touchend', onTouchEnd)

    handle.addEventListener('click', () => {
      if (!isDragging) {
        this.toggle()
      }
    })
  }

  toggle() {
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    this.element.classList.add('open')
    this.isOpen = true
    this.element.style.height = ''
  }

  close() {
    this.element.classList.remove('open')
    this.isOpen = false
    this.element.style.height = ''
    this._hideAllStates()
  }

  showLoading() {
    this._hideAllStates()
    const loadingState = this.element.querySelector('.loading-state')
    loadingState.style.display = 'flex'
    this.open()
  }

  showResult(data) {
    this._hideAllStates()
    this.currentAnalysisData = data

    const resultState = this.element.querySelector('.result-state')
    const resultText = resultState.querySelector('.result-text')
    resultText.innerHTML = data.analysisText.replace(/\n/g, '<br>')

    this.disclaimer.update()

    resultState.style.display = 'flex'
    this.open()
  }

  showError(message) {
    this._hideAllStates()
    const errorState = this.element.querySelector('.error-state')
    errorState.querySelector('.error-text').textContent = message
    errorState.style.display = 'flex'
    this.open()
  }

  showEmptyHint() {
    this._hideAllStates()
    const emptyState = this.element.querySelector('.empty-state')
    emptyState.style.display = 'flex'
    this.open()
  }

  _hideAllStates() {
    const states = this.element.querySelectorAll('.state')
    states.forEach((state) => {
      state.style.display = 'none'
    })
  }

  updateLanguage() {
    if (!this.element) return

    this.element.setAttribute('aria-label', i18n.t('analysis.title'))

    const title = this.element.querySelector('.panel-title')
    if (title) {
      title.textContent = i18n.t('analysis.title')
    }

    const closeBtn = this.element.querySelector('.close-btn')
    if (closeBtn) {
      closeBtn.setAttribute('aria-label', i18n.t('common.close'))
    }

    const emptyText = this.element.querySelector('.empty-text')
    if (emptyText) {
      emptyText.textContent = i18n.t('analysis.hint')
    }

    const loadingText = this.element.querySelector('.loading-text')
    if (loadingText) {
      loadingText.textContent = i18n.t('analysis.loading')
    }

    const bookBtn = this.element.querySelector('.book-btn')
    if (bookBtn) {
      bookBtn.textContent = i18n.t('consultation.bookButton')
    }

    this.disclaimer.update()
  }
}
